<template>
    <section class="content">
        <section class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1>{{ isEditMode ? 'Editar Promoción: ' + form.codigo : 'Nueva Promoción' }}</h1>
                    </div>
                    <div class="col-sm-6 text-right">
                        <span v-if="isEditMode" :class="statusBadgeClass" class="badge p-2">
                            {{ form.estado.toUpperCase() }}
                        </span>
                    </div>
                </div>
            </div>
        </section>

        <div class="container-fluid">
            <ul class="d-flex w-100 p-2 m-0 list-unstyled justify-content-between">
                <li v-for="s in steps" :key="s.id" class="d-flex align-items-center" :style="{ flex: s.id < steps.length ? 1 : '0' }">
                    <a href="javascript:void(0)" @click.prevent="goStep(s.id)"
                        class="rounded-circle border d-flex justify-content-center align-items-center shadow-sm" 
                        :class="paso >= s.id ? 'bg-navy text-white border-success' : 'text-secondary bg-white'" 
                        style="width: 2.8rem; height: 2.8rem; font-weight: bold;">
                        {{ s.id }}
                    </a>
                    <div v-if="s.id < steps.length" class="flex-grow-1 mx-2" :class="paso > s.id ? 'bg-success' : 'bg-secondary'" style="height: 3px;"></div>
                </li>
            </ul>

            <div v-if="paso === 1" ref="step1" class="card card-navy card-outline shadow">
                <div class="card-header"><h3 class="card-title font-weight-bold">1. Configuración Básica</h3></div>
                <div class="card-body">
                    <div class="form-row">
                        <div class="form-group col-md-6">
                            <label>Código <span class="text-danger" v-if="!isEditMode">*</span></label>
                            <input type="text" class="form-control" v-model="form.codigo" :disabled="isEditMode" required placeholder="Ej: PROMO-2024-X" />
                            <small v-if="isEditMode" class="text-muted">El código no se puede modificar.</small>
                        </div>
                        <div class="form-group col-md-6">
                            <label>Descripción *</label>
                            <input type="text" class="form-control" v-model="form.descripcion" required />
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group col-md-4">
                            <label>Modo de Promoción</label>
                            <select class="form-control" v-model="form.modo" :disabled="isEditMode">
                                <option value="descuento">Descuento Directo</option>
                                <option value="obsequio">Obsequio de Producto</option>
                            </select>
                        </div>

                        <template v-if="form.modo === 'descuento'">
                            <div class="form-group col-md-4">
                                <label>Tipo de Valor</label>
                                <select class="form-control" v-model="form.tipo_valor" required>
                                    <option value="porcentaje">Porcentaje</option>
                                    <option value="precio_fijo">Precio Fijo</option>
                                </select>
                            </div>
                            <div class="form-group col-md-4">
                                <label>Valor</label>
                                <div class="input-group">
                                    <input type="number" step="0.01" class="form-control" v-model.number="form.valor" required />
                                    <div class="input-group-append">
                                        <span class="input-group-text">{{ form.tipo_valor === 'porcentaje' ? '%' : '$' }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <div class="form-group col-md-4">
                                <label>% Calculado de Obsequio</label>
                                <input type="number" class="form-control" v-model.number="form.obsequio_porcentaje" min="1" max="100" required />
                            </div>
                            <div class="form-group col-md-4">
                                <label>Producto a Regalar</label>
                                <select ref="selectObsequio" class="form-control select2" v-model="form.obsequio_producto_id">
                                    <option value="">Seleccionar Producto</option>
                                    <option v-for="p in productosSelect" :key="p.id" :value="p.id">{{ p.codigo }} - {{ p.descripcion }}</option>
                                </select>
                            </div>
                        </template>
                    </div>

                    <div class="form-row border-top pt-3 mt-2">
                        <div class="form-group col-md-4">
                            <label>Fecha Inicio *</label>
                            <input type="date" class="form-control" v-model="form.fecha_inicio" required />
                        </div>
                        <div class="form-group col-md-4">
                            <label>Fecha Fin (Opcional)</label>
                            <input type="date" class="form-control" v-model="form.fecha_fin" />
                        </div>
                        <div class="form-group col-md-4">
                            <label>Estado del Sistema</label>
                            <select class="form-control" v-model="form.estado">
                                <option value="activa">Activa</option>
                                <option value="pausada">Pausada (Temporal)</option>
                                <option value="inactiva">Inactiva (Finalizada)</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-row bg-light p-2 rounded">
                        <div class="form-group col-md-6 mb-0">
                            <label>Monto Mínimo de Compra ($)</label>
                            <input type="number" step="0.01" class="form-control" v-model.number="form.min_monto" placeholder="0.00 = Sin mínimo" />
                        </div>
                        <div class="form-group col-md-6 mb-0">
                            <label>Cantidad Mínima de Unidades</label>
                            <input type="number" class="form-control" v-model.number="form.min_cantidad" placeholder="1 = Sin mínimo" />
                        </div>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-between">
                    <button class="btn btn-outline-danger" @click="cancel">Volver</button>
                    <button class="btn btn-primary px-4" @click="nextStep">Siguiente <i class="fas fa-arrow-right ml-1"></i></button>
                </div>
            </div>

            <div v-if="paso === 2" class="card card-navy card-outline shadow">
                <div class="card-header"><h3 class="card-title font-weight-bold">2. Alcance de Clientes</h3></div>
                <div class="card-body">
                    <div class="form-group p-3 border rounded bg-light mb-4">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" id="allClientsSwitch" v-model="form.all_clients" />
                            <label class="custom-control-label h6 mb-0" for="allClientsSwitch">
                                Habilitar para <strong>TODOS</strong> los clientes de Comercial Kurdamovil
                            </label>
                        </div>
                    </div>

                    <div v-show="!form.all_clients" class="table-responsive">
                        <table class="table table-sm table-hover table-bordered">
                            <thead class="thead-light">
                                <tr>
                                    <th class="text-center" style="width: 50px">
                                        <input type="checkbox" @change="toggleAllClients" :checked="allSelectedClients" />
                                    </th>
                                    <th>Código</th>
                                    <th>Nombre del Cliente</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="c in clients" :key="c.id" @click="toggleClient(c.id)" style="cursor: pointer">
                                    <td class="text-center"><input type="checkbox" :value="c.id" v-model="form.clientes" @click.stop /></td>
                                    <td><code>{{ c.codigo }}</code></td>
                                    <td>{{ c.nombre }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-between">
                    <button class="btn btn-outline-secondary" @click="prevStep"><i class="fas fa-arrow-left mr-1"></i> Atrás</button>
                    <button class="btn btn-primary px-4" @click="nextStep">Siguiente <i class="fas fa-arrow-right ml-1"></i></button>
                </div>
            </div>

            <div v-if="paso === 3" class="card card-navy card-outline shadow">
                <div class="card-header"><h3 class="card-title font-weight-bold">3. Selección de Productos</h3></div>
                <div class="card-body">
                    <div class="form-group p-3 border rounded bg-light mb-4">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" id="allProductsSwitch" class="custom-control-input" v-model="form.all_products">
                            <label class="custom-control-label h6 mb-0" for="allProductsSwitch">Aplica a todo el inventario</label>
                        </div>
                    </div>

                    <div v-show="!form.all_products">
                        <table id="productosStep3" class="table table-bordered table-striped w-100"></table>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-between">
                    <button class="btn btn-outline-secondary" @click="prevStep"><i class="fas fa-arrow-left mr-1"></i> Atrás</button>
                    <button class="btn btn-primary px-4" @click="nextStep">Siguiente <i class="fas fa-arrow-right ml-1"></i></button>
                </div>
            </div>

            <div v-if="paso === 4" class="card card-success card-outline shadow">
                <div class="card-header"><h3 class="card-title font-weight-bold">4. Resumen Final</h3></div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-6">
                            <h5 class="text-navy border-bottom pb-1">Configuración</h5>
                            <dl class="row">
                                <dt class="col-4">Código:</dt> <dd class="col-8"><code>{{ form.codigo }}</code></dd>
                                <dt class="col-4">Modo:</dt> <dd class="col-8"><span class="badge badge-info">{{ form.modo }}</span></dd>
                                <dt class="col-4">Validez:</dt> <dd class="col-8">{{ form.fecha_inicio }} al {{ form.fecha_fin || 'Indefinido' }}</dd>
                                <dt class="col-4">Mínimos:</dt> <dd class="col-8">{{ form.min_monto ? '$'+form.min_monto : 'Sin monto' }} / {{ form.min_cantidad || 'Sin cant.' }} unid.</dd>
                            </dl>
                        </div>
                        <div class="col-md-6">
                            <h5 class="text-navy border-bottom pb-1">Alcance</h5>
                            <p><strong>Clientes:</strong> {{ form.all_clients ? 'Todos' : form.clientes.length + ' seleccionados' }}</p>
                            <p><strong>Productos:</strong> {{ form.all_products ? 'Todos' : form.productos.length + ' seleccionados' }}</p>
                            <div v-if="form.modo === 'obsequio'" class="alert alert-info py-2">
                                <strong>Regalo:</strong> {{ mostrarProducto() }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-footer d-flex justify-content-between bg-white border-top">
                    <button class="btn btn-outline-secondary" @click="prevStep"><i class="fas fa-arrow-left mr-1"></i> Atrás</button>
                    <button class="btn btn-success btn-lg px-5 shadow" @click="submitPromocion" :disabled="loading">
                        <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
                        {{ isEditMode ? 'Guardar Cambios' : 'Crear Promoción' }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import api from "@/services/api";
import Swal from 'sweetalert2';

export default {
    props: {
        id: { type: [String, Number], default: null }
    },
    data() {
        return {
            paso: 1,
            loading: false,
            isEditMode: !!this.id,
            steps: [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }],
            clients: [],
            productosList: [],
            productosSelect: [],
            productosTable: null,
            form: {
                codigo: "",
                descripcion: "",
                modo: "descuento",
                tipo_valor: "porcentaje",
                valor: null,
                obsequio_porcentaje: null,
                obsequio_producto_id: '',
                fecha_inicio: "",
                fecha_fin: null,
                estado: "activa",
                min_monto: null,
                min_cantidad: null,
                all_clients: false,
                clientes: [],
                all_products: false,
                productos: []
            }
        };
    },
    computed: {
        allSelectedClients() {
            return this.clients.length > 0 && this.form.clientes.length === this.clients.length;
        },
        statusBadgeClass() {
            return {
                'badge-success': this.form.estado === 'activa',
                'badge-warning': this.form.estado === 'pausada',
                'badge-danger': this.form.estado === 'inactiva'
            }
        }
    },
    async mounted() {
        await this.fetchClients();
        await this.fetchProductos();
        
        if (this.isEditMode) {
            await this.loadPromocionData();
        }
    },
    watch: {
        paso(newStep) {
            if (newStep === 3) {
                this.$nextTick(() => this.initProductosTable());
            }
        },
        'form.modo': {
            handler() {
                this.$nextTick(() => this.initSelect2());
            },
            immediate: true
        }
    },
    methods: {
        async loadPromocionData() {
            try {
                this.loading = true;
                const { data } = await api.get(`/promociones/${this.id}`);
                
                const fechaIni = data.fecha_inicio ? data.fecha_inicio.split('T')[0] : "";
                const fechaFin = data.fecha_fin ? data.fecha_fin.split('T')[0] : "";

                // Mapeo de datos básicos
                Object.assign(this.form, {
                    ...data,
                    fecha_inicio: fechaIni,
                    fecha_fin: fechaFin,
                    clientes: data.clientes.map(c => c.id),
                    productos: data.productos.map(p => p.id),
                    all_products: !!data.all_products
                });

                // Reiniciar Select2 para el obsequio si aplica
                if (this.form.modo === 'obsequio') {
                    this.$nextTick(() => this.initSelect2());
                }
            } catch (error) {
                Swal.fire('Error', 'No se pudo cargar la promoción', 'error');
            } finally {
                this.loading = false;
            }
        },

        initSelect2() {
            if (this.form.modo !== 'obsequio') return;
            const $el = $(this.$refs.selectObsequio);
            $el.select2({ theme: "bootstrap4", width: '100%' })
               .on("select2:select", (e) => { this.form.obsequio_producto_id = e.params.data.id; });
            
            if (this.form.obsequio_producto_id) {
                $el.val(this.form.obsequio_producto_id).trigger('change');
            }
        },

        async fetchClients() {
            const { data } = await api.get("/clientes");
            this.clients = data.filter(c => Number(c.estatus) !== 0);
        },

        async fetchProductos() {
            const { data } = await api.get("/producto");
            this.productosSelect = data.filter(p => Number(p.estatus) !== 0);
            this.productosList = this.productosSelect;
        },

        initProductosTable() {
            const vm = this;
            this.productosTable = $("#productosStep3").DataTable({
                data: this.productosList,
                destroy: true,
                columns: [
                    {
                        title: "Selección",
                        data: null,
                        orderable: false,
                        className: 'text-center',
                        render: (row) => {
                            const isChecked = vm.form.productos.includes(row.id) ? "checked" : "";
                            return `<input type="checkbox" class="prod-check" value="${row.id}" ${isChecked}>`;
                        }
                    },
                    { title: "Código", data: "codigo" },
                    { title: "Descripción", data: "descripcion" }
                ],
                language: { url: 'https://cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json' },
                dom: '<"row mb-2"<"col-md-6"f>>t<"row mt-2"<"col-md-12 text-center"p>>',
            });

            $("#productosStep3").on("change", ".prod-check", function() {
                const id = parseInt(this.value);
                if (this.checked) {
                    if (!vm.form.productos.includes(id)) vm.form.productos.push(id);
                } else {
                    vm.form.productos = vm.form.productos.filter(x => x !== id);
                }
            });
        },

        toggleAllClients() {
            this.form.clientes = this.allSelectedClients ? [] : this.clients.map(c => c.id);
        },

        toggleClient(id) {
            const index = this.form.clientes.indexOf(id);
            if (index > -1) this.form.clientes.splice(index, 1);
            else this.form.clientes.push(id);
        },

        mostrarProducto() {
            const p = this.productosSelect.find(prod => prod.id == this.form.obsequio_producto_id);
            return p ? `${p.codigo} - ${p.descripcion}` : 'No seleccionado';
        },

        async submitPromocion() {
            try {
                this.loading = true;
                
                // 1. Preparamos el payload con limpieza profunda
                const payload = { 
                    ...this.form,
                    // Forzamos que valor sea número o null (no string vacío)
                    valor: (this.form.valor === "" || this.form.valor === null) ? 0 : Number(this.form.valor),
                    
                    // Limpiamos montos y cantidades
                    min_monto: this.form.min_monto === "" || this.form.min_monto === null ? null : Number(this.form.min_monto),
                    min_cantidad: this.form.min_cantidad === "" || this.form.min_cantidad === null ? null : Number(this.form.min_cantidad),
                    
                    // Aseguramos formato de fecha YYYY-MM-DD
                    fecha_inicio: this.form.fecha_inicio ? this.form.fecha_inicio.split('T')[0] : null,
                    fecha_fin: this.form.fecha_fin ? this.form.fecha_fin.split('T')[0] : null,
                };

                // 2. Limpieza lógica estricta según el modo
                if (payload.modo === 'descuento') {
                    if (!['porcentaje', 'precio_fijo'].includes(payload.tipo_valor)) {
                        payload.tipo_valor = 'porcentaje';
                    }
                    payload.obsequio_porcentaje = null;
                    payload.obsequio_producto_id = null;
                } else {
                    payload.tipo_valor = null;
                    payload.valor = null;
                }

                const url = this.isEditMode ? `/promociones/${this.id}` : '/promociones';
                const method = this.isEditMode ? 'put' : 'post';

                const { data } = await api[method](url, payload);

                await Swal.fire({
                    icon: 'success',
                    title: this.isEditMode ? '¡Actualizado!' : '¡Creado!',
                    text: 'La promoción de Kurdamovil se guardó con éxito.',
                    timer: 2000,
                    showConfirmButton: false
                });

                this.$router.push('/promociones');
            } catch (err) {
                // --- AQUÍ CAPTAMOS LOS ERRORES ---
                if (err.response && err.response.status === 422) {
                    const listaErrores = err.response.data.detalles;
                    
                    // Convertimos el objeto de errores en una lista HTML legible
                    let mensajeError = '<ul class="text-left">';
                    Object.values(listaErrores).forEach(mensajes => {
                        mensajes.forEach(m => {
                            mensajeError += `<li>${m}</li>`;
                        });
                    });
                    mensajeError += '</ul>';

                    Swal.fire({
                        icon: 'error',
                        title: 'Error de validación',
                        html: mensajeError, // Usamos HTML para mostrar la lista
                        confirmButtonColor: '#001f3f' // Color Navy de tu tema
                    });
                } else {
                    // Error genérico (servidor caído, error 500, etc)
                    Swal.fire({
                        icon: 'error',
                        title: 'Error inesperado',
                        text: 'Hubo un problema con el servidor. Inténtalo más tarde.',
                    });
                    console.error("Error completo:", err);
                }
            } finally {
                this.loading = false;
            }
        },

        nextStep() {
            if (this.paso === 1 && !this.validarPaso1()) return;
            if (this.paso < 4) this.paso++;
        },
        prevStep() { if (this.paso > 1) this.paso--; },
        goStep(n) { if (n < this.paso) this.paso = n; },
        cancel() { this.$router.push("/promociones"); },
        
        validarPaso1() {
            const inputs = this.$refs.step1.querySelectorAll('input[required], select[required]');
            let valido = true;
            inputs.forEach(i => {
                if (!i.checkValidity()) {
                    i.classList.add('is-invalid');
                    valido = false;
                } else {
                    i.classList.remove('is-invalid');
                }
            });
            return valido;
        }
    }
};
</script>

<style scoped>
.is-invalid { border-color: #dc3545 !important; }
.card-navy.card-outline { border-top: 3px solid #001f3f; }
.bg-navy { background-color: #001f3f !important; }
</style>